<template>
    <div class="modal-card card">
        <form>
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">Edit Deployment Jobs</h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="card-body">
                <!-- Custom Tabs Navigation -->
                <ul class="nav nav-tabs custom-modal-tabs" role="tablist">
                    <li v-for="tab in visibleTabs" :key="tab.name" class="nav-item" role="presentation">
                        <button class="nav-link d-flex align-items-center" :class="{ active: activeTab === tab.name }"
                            @click="setActiveTab(tab.name)" type="button" role="tab">
                            <i v-if="tab.icon" :class="[`fa fa-${tab.icon}`, 'me-2']"></i>
                            <span>{{ tab.label || tab.name }}</span>
                            <i v-if="tab.name === 'config-changes'" class="fa fa-times ms-3"
                                @click.stop="closeConfigChangesTab" style="cursor: pointer; font-size: 0.85rem;"
                                aria-label="Close tab"></i>
                        </button>
                    </li>
                </ul>

                <!-- Tab Content -->
                <div class="tab-content">
                    <div v-for="tab in visibleTabs" :key="tab.name" class="tab-pane fade"
                        :class="{ 'show active': activeTab === tab.name }" role="tabpanel">
                        <div v-if="activeTab === tab.name && tab.name === 'edit'" class="edit-tab-content">
                            <div v-if="jobReloadInProgress">
                                <div class="d-flex justify-content-center align-items-center" style="height: 100px;">
                                    <div class="spinner-border spinner-border-sm me-2" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    <span>Loading jobs data...</span>
                                </div>
                            </div>
                            <div v-else>
                                <div>
                                    <div class="mb-4">
                                        <!-- dropdown to select the prod v2 sandbox -->
                                        <label for="destinationSandbox">
                                            <h4>Destination Sandbox Name
                                                <span class="fa fa-circle-info text-muted"
                                                    title="Select the destination sandbox name for the promotion."
                                                    data-bs-toggle="tooltip" data-bs-placement="right"></span>
                                            </h4>
                                        </label>
                                        <select id="destinationSandbox" class="form-select mt-1"
                                            v-model="destinationSandbox">
                                            <option v-for="sandbox in sandboxListOptions" :key="sandbox.value"
                                                :value="sandbox.value">
                                                {{ sandbox.label }}
                                            </option>
                                        </select>
                                    </div>
                                    <h4 class="mb-4 mt-2">
                                        Configuration
                                        <span class="fa fa-circle-info text-muted"
                                            title="Configure values for all jobs created in this deployment. The changes will be updated in vars.yaml file for each job."
                                            data-bs-toggle="tooltip" data-bs-placement="right"></span>
                                    </h4>
                                    <ClusterConfigEasyMode ref="clusterConfigComponent"
                                        :key="'cluster-config-' + (jobs ? jobs.length : 0) + '-' + (jobReloadInProgress ? 'loading' : 'loaded')"
                                        v-model="clusterConfig" :fieldInfo="clusterConfigFieldInfo"
                                        :cleanConfigFn="cleanClusterConfig"
                                        @invalidValuesChange="onInvalidValuesChange" />
                                    <div v-if="oltp" class="mt-5">
                                        <hr class="my-3">
                                        <h4 class="my-4">
                                            OLTP
                                            <span class="fa fa-circle-info text-muted"
                                                title="Configure the OLTP fields." data-bs-toggle="tooltip"
                                                data-bs-placement="right"></span>
                                        </h4>
                                        <OltpConfigEasyMode ref="oltpConfigComponent"
                                            :key="'oltp-config-' + (jobs ? jobs.length : 0) + '-' + (jobReloadInProgress ? 'loading' : 'loaded')"
                                            v-model="oltpConfig" :fieldInfo="oltpConfigFieldInfo"
                                            :cleanConfigFn="cleanOltpConfig" />
                                    </div>
                                    <div v-if="hasExtractConfig && extractConfig" class="mt-5">
                                        <hr class="my-3">
                                        <h4 class="my-4">
                                            Edge Extract Configuration
                                            <span class="fa fa-circle-info text-muted"
                                                title="Configure the tenant_id and extract fields."
                                                data-bs-toggle="tooltip" data-bs-placement="right"></span>
                                        </h4>
                                        <EasyMode ref="extractConfigComponent"
                                            :key="'extract-config-' + (jobs ? jobs.length : 0) + '-' + (jobReloadInProgress ? 'loading' : 'loaded')"
                                            v-model="extractConfig" :editMode="true" :invalidValues="invalidValues" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="activeTab === tab.name && tab.name === 'config-changes'"
                            class="config-changes-tab-content">
                            <div v-if="configDiff.length > 0">
                                <div class="d-flex justify-content-between align-items-top mb-3">
                                    <div>
                                        <p class="mb-1" v-if="conflictDetails">
                                            <strong>Updated by:</strong>&nbsp;<span class="text-muted">{{
                                                conflictDetails.updated_by }}</span>
                                        </p>
                                        <p v-if="conflictDetails" class="mb-0">
                                            <strong>Updated at:</strong>&nbsp;<span class="text-muted">{{
                                                DateTimeFormat(conflictDetails.updated_at) }}</span>
                                        </p>
                                    </div>
                                    <button class="btn btn-sm btn-outline-danger" @click="closeConfigChangesTab"
                                        :style="{ height: '27px' }" type="button">
                                        <i class="fa fa-times me-1"></i> Close
                                    </button>
                                </div>
                                <div class="row mb-1">
                                    <div class="col-6">
                                        <p class="config-header text-primary fw-bold">Local Copy</p>
                                    </div>
                                    <div class="col-6">
                                        <p class="config-header text-primary fw-bold">Remote Copy</p>
                                    </div>
                                </div>
                                <div v-for="(diff, index) in configDiff" :key="index" class="mb-4">
                                    <p class="config-header text-primary fw-bold mb-2">
                                        {{ diff.jobName }}
                                        <i class="fa fa-arrow-right-long pt-1 mx-2" style="font-size: 0.7rem;"></i>
                                        {{ diff.prev.relative_path }}
                                    </p>
                                    <Diff mode="split" theme="light" language="plaintext" :prev="diff.prev.content"
                                        :current="diff.current.content" />
                                </div>
                            </div>
                            <div v-else class="text-center text-muted py-4">
                                <p>No configuration conflicts detected.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card-footer d-flex justify-content-between">
                <span class="text-danger" style="max-width: 750px; overflow-wrap: break-word;">
                    {{ activeTab === 'promote' ? promoteError : editError }}
                </span>
                <div>
                    <template v-if="activeTab === 'config-changes'">
                        <button class="btn btn-primary me-3" @click="onReloadConfig()" type="button">
                            <div v-if="reloadInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else>
                                Reload Changes
                            </div>
                        </button>
                        <button class="btn btn-primary" @click="onOverwriteConfig()" type="button">
                            <div v-if="overWriteInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else>
                                Overwrite Current Changes
                            </div>
                        </button>
                    </template>
                    <template v-else>
                        <button class="btn btn-success me-3" @click="onSaveClick()" type="button"
                            :disabled="saveInProgress || saveAndRunInProgress || !jobs || jobs.length === 0">
                            <div v-if="saveInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <span>Save</span>
                                <span class="fa fa-circle-info text-light" style="font-size: 0.85rem; margin-left: 5px;"
                                    title="Save the configuration changes for all jobs in this deployment. The changes will be updated in vars.yaml file for each job with UI markers."
                                    data-bs-toggle="tooltip" data-bs-placement="top">
                                </span>
                            </div>
                        </button>
                        <button class="btn btn-success me-3" @click="onSaveAndRunClick()" type="button"
                            :disabled="saveInProgress || saveAndRunInProgress || !jobs || jobs.length === 0">
                            <div v-if="saveAndRunInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <span>Save & Run</span>
                                <span class="fa fa-circle-info text-light" style="font-size: 0.85rem; margin-left: 5px;"
                                    title="Save the configuration changes and immediately run all jobs in this deployment. The changes will be updated in vars.yaml file for each job with UI markers."
                                    data-bs-toggle="tooltip" data-bs-placement="top">
                                </span>
                            </div>
                        </button>
                        <button class="btn btn-primary" @click="promoteRequiredJobs()" type="button"
                            :disabled="promotionInProgress || saveInProgress || saveAndRunInProgress || !jobs || jobs.length === 0">
                            <div v-if="promotionInProgress" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <span>Promote</span>
                                <span class="fa fa-circle-info text-light" style="font-size: 0.85rem; margin-left: 5px;"
                                    title="Promote the latest changes to production sandbox." data-bs-toggle="tooltip"
                                    data-bs-placement="top">
                                </span>
                            </div>
                        </button>
                    </template>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import ClusterConfigEasyMode from "../ClusterConfigEasyMode.vue";
import EasyMode from "../EasyMode.vue";
import OltpConfigEasyMode from "../OltpConfigEasyMode.vue";
import YAML from "yaml";
import axios from "axios";
import { mapActions } from 'vuex';
import { DateTimeFormat } from '../../utils/commonFunction';

export default {
    components: {
        ClusterConfigEasyMode,
        EasyMode,
        OltpConfigEasyMode,
    },
    data() {
        return {
            uiMarkerStart: '# === UI:CONFIGURABLE_VALUES:START ===',
            uiMarkerEnd: '# === UI:CONFIGURABLE_VALUES:END ===',
            editError: "",
            promoteError: "",
            saveInProgress: false,
            saveAndRunInProgress: false,
            jobs: null,
            jobNames: [],
            deployment: null,
            clusterConfig: null,
            extractConfig: null,
            hasExtractConfig: false,
            activeTab: "edit",
            invalidValues: ['null'],
            clusterConfigFieldInfo: {
                graph_ttl: {
                    placeholder: "inf",
                    editable: false,
                    tooltip: "This will hold the time to delete the node from its existing timestamp.",
                },
                create_cluster_weightage_map: {
                    editable: false,
                    tooltip: "This will provide the weightage value per type~domain. This will help with the algorithm of choosing the clusterID.",
                },
                invalid_values: { // part of edge extract file, just sending some info about that field
                    initialValue: ['null'], // for reset to initial value
                    editable: false,
                    tooltip: "These nodes will be dropped and sent to the quarantined list",
                },
                create_cluster_ttl_maps: {
                    editable: false,
                    tooltip: "This will provide the weightage value per type~domain. This will help with the algorithm of choosing the clusterID.",
                },
                choose_cluster_type: {
                    placeholder: "c_type",
                    editable: false,
                    tooltip: "This configured type will be assigned as the cluster's type.",
                },
                choose_cluster_domain: {
                    placeholder: "c_domain",
                    editable: false,
                    tooltip: "This configured domain will be assigned as the cluster's domain.",
                }
            },
            tabItems: [
                {
                    name: "edit",
                    label: "Edit",
                    slot: "edit-tab",
                    icon: "edit",
                    visible: true
                },
                {
                    name: "config-changes",
                    label: "Config Changes",
                    slot: "config-changes-tab",
                    icon: "code-compare",
                    visible: false
                }
            ],

            remoteJobsInfo: {},
            currentJobsInfo: {},
            configDiff: [],
            conflictDetails: null,
            overWriteInProgress: false,
            reloadInProgress: false,
            jobReloadInProgress: false,
            // Store anchor names per job per field: { jobName: { fieldName: anchorName } }
            jobAnchors: {},

            // promotion data
            destinationSandbox: null,
            promotions: {},
            promotionInProgress: false,

            // oltp section
            oltp: false,
            oltpConfig: null,
            oltpConfigFieldInfo: {
                push_all_nodes_to_oltp: {
                    editable: false,
                    tooltip: "The value to send ALL nodes to OLTP in the neighbours list with the clusterID as the partition key.",
                },
                push_all_nodes_to_oltp_rev: {
                    editable: false,
                    tooltip: "The value to send ALL nodes to OLTP. This will be mapped from NodeID (as partition key) to ClusterID (as neighbour).",
                },
                olap_reverse_cluster_nodes: {
                    editable: false,
                    tooltip: "The provided nodes will be pushed to OLTP in node -> clusterID format",
                },
                cluster_oltp_nodes: {
                    editable: false,
                    tooltip: "The provided nodes will be pushed to OLTP in clusterID -> NodeID format",
                },
            },

        }
    },
    computed: {
        visibleTabs() {
            return this.tabItems.filter(tab => tab.visible);
        },
        pipelineJob() {
            return this.jobs?.find(job => job.jobDetails?.kind.toLowerCase() === 'pipeline');
        },
        sandboxListOptions() {
            return this.$store.state.sandbox.prodV2Sandboxes ? this.$store.state.sandbox.prodV2Sandboxes.map((sandbox) => {
                return { "label": sandbox.name, "value": sandbox.name };
            }) : [];
        },
    },
    props: {
        modalData: Object,
    },
    beforeMount() {
        this.jobNames = JSON.parse(JSON.stringify(this.modalData?.jobNames || []));
        this.jobs = JSON.parse(JSON.stringify(this.modalData?.jobs));
        this.deployment = JSON.parse(JSON.stringify(this.modalData?.deployment));
        this.destinationSandbox = this.$store.state.sandbox.prodV2Sandboxes.length ? this.$store.state.sandbox.prodV2Sandboxes[0].name : ""
        this.loadConfig();
        this.initializePromoteTab();
    },
    methods: {
        ...mapActions('amdp', {
            readJob: 'readJob',
        }),
        DateTimeFormat,
        loadConfig() {
            // Initialize empty clusterConfig
            let extractedConfig = {
                graph_ttl: "",
                create_cluster_weightage_map: [], // array of objects with key and value
                create_cluster_ttl_maps: [], // array of objects with key and value
                choose_cluster_type: "",
                choose_cluster_domain: ""
            };
            // Initialize extract config
            let edgeExtractConfigObj = null;
            this.hasExtractConfig = false;
            // Initialize jobAnchors
            this.jobAnchors = {};

            // Initialize oltp config
            this.oltpConfig = {
                // push_all_nodes_to_oltp_rev and olap_reverse_cluster_nodes will be added below
                olap_reverse_cluster_nodes: [],
                cluster_oltp_nodes: [],
            };

            // Iterate through all jobs and extract config from vars.yaml
            if (this.jobs && this.jobs.length > 0) {
                this.jobs.forEach(job => {
                    if (job?.jobDetails?.system_files) {
                        // Find vars.yaml file
                        const varsYamlFile = job.jobDetails.system_files.find(
                            file => file.relative_path === 'vars.yaml'
                        );

                        if (varsYamlFile?.content) {
                            try {
                                // Extract the configurable values section between markers
                                const configSection = this.extractConfigSection(varsYamlFile.content);

                                if (configSection) {
                                    // Extract anchor names from the YAML text (before parsing)
                                    const jobName = job.jobDetails?.name || job.jobId;
                                    const anchors = this.extractAllAnchorsFromYaml(configSection);
                                    if (Object.keys(anchors).length > 0) {
                                        this.jobAnchors[jobName] = anchors;
                                    }

                                    // Parse the extracted section as simple YAML
                                    const parsedYaml = YAML.parse(configSection);

                                    if (parsedYaml && typeof parsedYaml === 'object') {
                                        if (parsedYaml.choose_cluster_type && !extractedConfig.choose_cluster_type) {
                                            extractedConfig.choose_cluster_type = parsedYaml.choose_cluster_type;
                                            this.clusterConfigFieldInfo.choose_cluster_type.editable = true;
                                        }

                                        if (parsedYaml.choose_cluster_domain && !extractedConfig.choose_cluster_domain) {
                                            extractedConfig.choose_cluster_domain = parsedYaml.choose_cluster_domain;
                                            this.clusterConfigFieldInfo.choose_cluster_domain.editable = true;
                                        }

                                        if (parsedYaml.graph_ttl && !extractedConfig.graph_ttl) {
                                            extractedConfig.graph_ttl = parsedYaml.graph_ttl;
                                            this.clusterConfigFieldInfo.graph_ttl.editable = true;
                                        }

                                        if (parsedYaml.create_cluster_weightage_map && extractedConfig.create_cluster_weightage_map.length === 0) {
                                            extractedConfig.create_cluster_weightage_map = Object.entries(parsedYaml.create_cluster_weightage_map).map(([key, value]) => {
                                                return {
                                                    key: key,
                                                    value: value
                                                };
                                            });
                                            this.clusterConfigFieldInfo.create_cluster_weightage_map.editable = true;
                                        }

                                        if (parsedYaml.create_cluster_ttl_maps && extractedConfig.create_cluster_ttl_maps.length === 0) {
                                            extractedConfig.create_cluster_ttl_maps = Object.entries(parsedYaml.create_cluster_ttl_maps).map(([key, value]) => {
                                                return {
                                                    key: key,
                                                    value: value
                                                };
                                            });
                                            this.clusterConfigFieldInfo.create_cluster_ttl_maps.editable = true;
                                        }

                                        // Check for tenant_id and extract fields
                                        if ((parsedYaml.tenant_id !== undefined || parsedYaml.extract !== undefined) && !edgeExtractConfigObj) {
                                            edgeExtractConfigObj = {};
                                            if (parsedYaml.tenant_id !== undefined) {
                                                edgeExtractConfigObj.tenant_id = parsedYaml.tenant_id;
                                            }
                                            if (parsedYaml.extract !== undefined) {
                                                edgeExtractConfigObj.extract = parsedYaml.extract;
                                            }
                                            this.hasExtractConfig = true;
                                        }

                                        // Check for oltp config
                                        if (Object.hasOwn(parsedYaml, 'push_all_nodes_to_oltp') && !Object.hasOwn(this.oltpConfig, 'push_all_nodes_to_oltp')) {
                                            this.oltpConfig.push_all_nodes_to_oltp = parsedYaml.push_all_nodes_to_oltp;
                                            this.oltpConfigFieldInfo.push_all_nodes_to_oltp.editable = true;
                                        }

                                        if (Object.hasOwn(parsedYaml, 'push_all_nodes_to_oltp_rev') && !Object.hasOwn(this.oltpConfig, 'push_all_nodes_to_oltp_rev')) {
                                            this.oltpConfig.push_all_nodes_to_oltp_rev = parsedYaml.push_all_nodes_to_oltp_rev;
                                            this.oltpConfigFieldInfo.push_all_nodes_to_oltp_rev.editable = true;
                                        }

                                        if (parsedYaml.olap_reverse_cluster_nodes && this.oltpConfig.olap_reverse_cluster_nodes.length === 0) {
                                            this.oltpConfig.olap_reverse_cluster_nodes = parsedYaml.olap_reverse_cluster_nodes;
                                            this.oltpConfigFieldInfo.olap_reverse_cluster_nodes.editable = true;
                                        }

                                        if (parsedYaml.cluster_oltp_nodes && this.oltpConfig.cluster_oltp_nodes.length === 0) {
                                            this.oltpConfig.cluster_oltp_nodes = parsedYaml.cluster_oltp_nodes;
                                            this.oltpConfigFieldInfo.cluster_oltp_nodes.editable = true;
                                        }
                                    }
                                }

                                // check pipeline job's vars file to skip oltp or not
                                if (job.jobDetails?.kind.toLowerCase() === 'pipeline') {
                                    let vars = YAML.parse(varsYamlFile.content);
                                    if (Object.hasOwn(vars, 'is_oltp') && vars.is_oltp === true) this.oltp = true;
                                    else if (Object.hasOwn(vars, 'not_oltp') && vars.not_oltp === false) this.oltp = true;
                                }
                            } catch (e) {
                                console.warn(`Failed to parse vars.yaml for job ${job.jobId}:`, e);
                            }
                        }
                    }
                });
            }
            // Create a new object reference to ensure reactivity
            this.clusterConfig = { ...extractedConfig };

            // set initial values for the cluster config fields
            this.clusterConfigFieldInfo.choose_cluster_type.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.choose_cluster_type));
            this.clusterConfigFieldInfo.choose_cluster_domain.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.choose_cluster_domain));
            this.clusterConfigFieldInfo.graph_ttl.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.graph_ttl));
            this.clusterConfigFieldInfo.create_cluster_weightage_map.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.create_cluster_weightage_map));
            this.clusterConfigFieldInfo.create_cluster_ttl_maps.initialValue = JSON.parse(JSON.stringify(this.clusterConfig.create_cluster_ttl_maps));

            // set initial values for the oltp config fields
            if (Object.hasOwn(this.oltpConfig, 'push_all_nodes_to_oltp')) {
                this.oltpConfigFieldInfo.push_all_nodes_to_oltp.initialValue = JSON.parse(JSON.stringify(this.oltpConfig.push_all_nodes_to_oltp));
            }
            if (Object.hasOwn(this.oltpConfig, 'push_all_nodes_to_oltp_rev')) {
                this.oltpConfigFieldInfo.push_all_nodes_to_oltp_rev.initialValue = JSON.parse(JSON.stringify(this.oltpConfig.push_all_nodes_to_oltp_rev));
            }
            this.oltpConfigFieldInfo.olap_reverse_cluster_nodes.initialValue = JSON.parse(JSON.stringify(this.oltpConfig.olap_reverse_cluster_nodes));
            this.oltpConfigFieldInfo.cluster_oltp_nodes.initialValue = JSON.parse(JSON.stringify(this.oltpConfig.cluster_oltp_nodes));

            // Set extract config as YAML string if it exists
            if (edgeExtractConfigObj) {
                this.extractConfig = YAML.stringify(edgeExtractConfigObj);
            } else {
                this.extractConfig = null;
            }

            this.clusterConfigFieldInfo.invalid_values.editable = this.hasExtractConfig && this.extractConfig ? true : false;
        },
        setActiveTab(tabName) {
            this.activeTab = tabName;
        },
        extractConfigSection(yamlContent) {
            if (!yamlContent || typeof yamlContent !== 'string') {
                return null;
            }

            const startMarker = this.uiMarkerStart;
            const endMarker = this.uiMarkerEnd;

            const startIndex = yamlContent.indexOf(startMarker);
            const endIndex = yamlContent.indexOf(endMarker);

            if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
                return null;
            }

            // Extract the section between markers (excluding the markers themselves)
            const sectionStart = startIndex + startMarker.length;
            const sectionEnd = endIndex;
            const section = yamlContent.substring(sectionStart, sectionEnd).trim();

            return section || null;
        },
        /**
         * Check if a job has UI markers with fields defined in clusterConfigFieldInfo
         * @param {Object} job - Job object with jobDetails
         * @returns {boolean} - True if job has UI markers with any clusterConfigFieldInfo fields
         */
        hasUIMarkersWithValues(job) {
            if (!job?.jobDetails?.system_files) {
                return false;
            }

            // Find vars.yaml file
            const varsYamlFile = job.jobDetails.system_files.find(
                file => file.relative_path === 'vars.yaml'
            );

            if (!varsYamlFile?.content) {
                return false;
            }

            // Extract the configurable values section between markers
            const configSection = this.extractConfigSection(varsYamlFile.content);

            if (!configSection) {
                return false;
            }

            try {
                // Parse the extracted section as YAML
                const parsedYaml = YAML.parse(configSection);

                if (!parsedYaml || typeof parsedYaml !== 'object') {
                    return false;
                }

                // Get all field names from clusterConfigFieldInfo
                const fieldInfoKeys = Object.keys(this.clusterConfigFieldInfo);

                // Check if any of the parsed YAML keys match fields in clusterConfigFieldInfo
                const hasFieldInfoFields = Object.keys(parsedYaml).some(key => {
                    return fieldInfoKeys.includes(key);
                });

                return hasFieldInfoFields;
            } catch (e) {
                console.warn(`Failed to parse config section for job ${job.jobId}:`, e);
                return false;
            }
        },
        /**
         * Extract all anchor names from YAML text for all configurable fields using parseDocument
         * @param {string} yamlText - YAML text content
         * @returns {Object} - Object with field names as keys and anchor names as values
         */
        extractAllAnchorsFromYaml(yamlText) {
            const anchors = {};
            if (!yamlText || typeof yamlText !== 'string') {
                return anchors;
            }

            try {
                // Use parseDocument to parse YAML and preserve anchor information
                const doc = YAML.parseDocument(yamlText);
                if (!doc || !doc.contents) {
                    return anchors;
                }

                // Get the root mapping node
                const rootNode = doc.contents;

                // Check if it's a map (kind can be 'MAP' or we check for items property)
                if (!rootNode.items || !Array.isArray(rootNode.items)) {
                    return anchors;
                }

                // Iterate through all items in the YAML map
                rootNode.items.forEach(item => {
                    if (item && item.key && item.value) {
                        const fieldName = item.key.value;
                        const valueNode = item.value;

                        // Check if the value node has an anchor
                        if (valueNode.anchor) {
                            anchors[fieldName] = valueNode.anchor;
                        }
                    }
                });
            } catch (e) {
                console.warn('Failed to parse YAML document for anchor extraction:', e);
            }
            return anchors;
        },
        onInvalidValuesChange(invalidValues) {
            this.invalidValues = invalidValues;
        },
        async onSaveClick() {
            await this.saveConfiguration(this.clusterConfig, this.extractConfig);
        },
        async onSaveAndRunClick() {
            await this.saveConfigurationAndRun(this.clusterConfig, this.extractConfig);
        },
        cleanClusterConfig(config) {
            // Helper method to clean cluster config
            if (!config || typeof config !== 'object') {
                return {};
            }

            const cleaned = {};

            if (config.graph_ttl) cleaned.graph_ttl = config.graph_ttl;
            if (config.choose_cluster_type) cleaned.choose_cluster_type = config.choose_cluster_type;
            if (config.choose_cluster_domain) cleaned.choose_cluster_domain = config.choose_cluster_domain;

            if (config.create_cluster_weightage_map && config.create_cluster_weightage_map.length > 0) {
                const weightageMap = [];
                config.create_cluster_weightage_map.forEach(item => {
                    if (item.key !== undefined && item.key !== null && item.value !== undefined && item.value !== null) {
                        let mapItem = JSON.parse(JSON.stringify(item));
                        weightageMap.push(mapItem);
                    }
                });
                cleaned.create_cluster_weightage_map = weightageMap;
            }

            if (config.create_cluster_ttl_maps && config.create_cluster_ttl_maps.length > 0) {
                const ttlMaps = [];
                config.create_cluster_ttl_maps.forEach(item => {
                    if (item.key !== undefined && item.key !== null && item.value !== undefined && item.value !== null) {
                        let mapItem = JSON.parse(JSON.stringify(item));
                        if (String(mapItem.value) == "-1") {
                            mapItem.value = "inf";
                        } else {
                            // add 'h' to the end
                            mapItem.value = String(mapItem.value) + "h";
                        }
                        ttlMaps.push(mapItem);
                    }
                });
                cleaned.create_cluster_ttl_maps = ttlMaps;
            }

            return cleaned;
        },
        cleanOltpConfig(config) {
            // Helper method to clean oltp config
            if (!config || typeof config !== 'object') {
                return {};
            }

            const cleaned = {};
            if (Object.hasOwn(config, 'push_all_nodes_to_oltp') && typeof config.push_all_nodes_to_oltp === 'boolean') cleaned.push_all_nodes_to_oltp = config.push_all_nodes_to_oltp;
            if (Object.hasOwn(config, 'push_all_nodes_to_oltp_rev') && typeof config.push_all_nodes_to_oltp_rev === 'boolean') cleaned.push_all_nodes_to_oltp_rev = config.push_all_nodes_to_oltp_rev;

            // Only include array if it has items
            if (Array.isArray(config.olap_reverse_cluster_nodes) && config.olap_reverse_cluster_nodes.length > 0) {
                cleaned.olap_reverse_cluster_nodes = config.olap_reverse_cluster_nodes;
            }
            if (Array.isArray(config.cluster_oltp_nodes) && config.cluster_oltp_nodes.length > 0) {
                cleaned.cluster_oltp_nodes = config.cluster_oltp_nodes;
            }

            return cleaned;
        },
        convertTTLandWeightageToMap(config) {
            if (config.create_cluster_weightage_map?.length > 0) {
                let weightageMap = {};
                config.create_cluster_weightage_map.forEach(item => {
                    weightageMap[item.key] = parseFloat(String(item.value));
                });
                config.create_cluster_weightage_map = weightageMap;
            }
            if (config.create_cluster_ttl_maps?.length > 0) {
                let ttlMaps = {};
                config.create_cluster_ttl_maps.forEach(item => {
                    ttlMaps[item.key] = item.value;
                });
                config.create_cluster_ttl_maps = ttlMaps;
            }
            return config;
        },
        async saveConfiguration(config, extractConfig) {
            this.editError = "";
            this.saveInProgress = true;

            try {
                config = this.convertTTLandWeightageToMap(config);
                const result = await this.updateJobsConfig(config, this.oltpConfig, extractConfig);

                // If there are conflicts, the config changes tab will be shown automatically
                if (result.hasConflicts) {
                    this.$store.dispatch("addToast", {
                        message: "Configuration conflicts detected. Please review the changes.",
                        type: "warning",
                    });
                    this.saveInProgress = false;
                    return;
                }

                if (result.total === 0) {
                    this.editError = "No jobs found with configurable values in markers.";
                } else if (result.successful === result.total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved successfully for all ${result.total} job(s).`,
                        type: "success",
                    });
                    // update jobs in detail view
                    if (this.modalData?.onRefreshJobs && typeof this.modalData.onRefreshJobs === 'function') {
                        this.modalData.onRefreshJobs();
                    }
                    // update jobs in modal and load config
                    await this.refreshJobsData();
                    this.loadConfig();
                    this.initializePromoteTab(); // to update vars.yaml content in promote tab
                } else if (result.successful > 0) {
                    this.editError = `Failed to update ${result.total - result.successful} out of ${result.total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved for ${result.successful} job(s), but ${result.total - result.successful} job(s) failed.`,
                        type: "warning",
                    });
                } else {
                    this.editError = `Failed to update all ${result.total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: this.editError,
                        type: "error",
                    });
                }
            } catch (error) {
                this.editError = error.response?.data?.error || error.message || "Failed to save configuration";
                this.$store.dispatch("addToast", {
                    message: this.editError || "Something went wrong when saving configuration. Please contact support.",
                    type: "error",
                });
            } finally {
                this.saveInProgress = false;
            }
        },
        async saveConfigurationAndRun(config, extractConfig) {
            this.editError = "";
            this.saveAndRunInProgress = true;

            try {
                config = this.convertTTLandWeightageToMap(config);
                const result = await this.updateJobsConfig(config, this.oltpConfig, extractConfig);

                // If there are conflicts, the config changes tab will be shown automatically
                if (result.hasConflicts) {
                    this.$store.dispatch("addToast", {
                        message: "Configuration conflicts detected. Please review the changes in the Config Changes tab.",
                        type: "warning",
                    });
                    this.saveAndRunInProgress = false;
                    return;
                }

                if (result.total === 0) {
                    this.editError = "No jobs found with configurable values in markers.";
                } else if (result.successful === result.total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved successfully for all job(s).`,
                        type: "success",
                    });

                    this.executePipelineJob();
                    this.$store.dispatch("hideModal");
                } else if (result.successful > 0) {
                    this.editError = `Failed to update ${result.total - result.successful} out of ${result.total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: `Configuration saved for ${result.successful} job(s), but ${result.total - result.successful} job(s) failed.`,
                        type: "warning",
                    });
                } else {
                    this.editError = `Failed to update all ${result.total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: this.editError,
                        type: "error",
                    });
                }
            } catch (error) {
                this.editError = error.response?.data?.error || error.message || "Failed to save configuration";
                this.$store.dispatch("addToast", {
                    message: this.editError || "Something went wrong when saving configuration. Please contact support.",
                    type: "error",
                });
            } finally {
                this.saveAndRunInProgress = false;
            }
        },
        /**
         * Update the config section in vars.yaml while preserving content outside markers and extra fields inside
         * @param {string} yamlContent - Original vars.yaml content
         * @param {Object} newConfig - New config values from UI
         * @param {string} jobName - Job name to get anchor names for
         * @param {string} extractConfigYaml - Extract config as YAML string (optional)
         * @returns {string} - Updated vars.yaml content
         */
        updateConfigSection(jobName, yamlContent, newConfig, oltpConfig, extractConfigYaml = null) {
            const startMarker = this.uiMarkerStart;
            const endMarker = this.uiMarkerEnd;

            const startIndex = yamlContent.indexOf(startMarker);
            const endIndex = yamlContent.indexOf(endMarker);

            // If markers don't exist, return original content
            if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
                return yamlContent;
            }

            // Extract content before and after markers
            const beforeSection = yamlContent.substring(0, startIndex);
            const afterSection = yamlContent.substring(endIndex + endMarker.length);

            // Extract and parse existing config section
            const sectionStart = startIndex + startMarker.length;
            const sectionEnd = endIndex;
            const existingSection = yamlContent.substring(sectionStart, sectionEnd).trim();

            // Get stored anchor names for this job, or extract from existing section if not stored
            let jobAnchors = this.jobAnchors[jobName] || {};
            if (!jobAnchors || Object.keys(jobAnchors).length === 0) {
                // Fallback: extract anchors from existing section if not already stored
                jobAnchors = this.extractAllAnchorsFromYaml(existingSection);
            }

            let existingConfig = {};
            if (existingSection) {
                try {
                    existingConfig = YAML.parse(existingSection) || {};
                } catch (e) {
                    console.warn('Failed to parse existing config section:', e);
                    existingConfig = {};
                }
            }

            // Only update fields that already exist in the config section
            // Filter newConfig to only include keys that exist in existingConfig
            // This ensures we don't add new fields, only update existing ones
            const filteredNewConfig = {};
            Object.keys(newConfig).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(existingConfig, key)) {
                    // Only update if the field exists in the original config
                    // Update even if the new value is empty/null (user might want to clear it)
                    filteredNewConfig[key] = newConfig[key];
                }
            });

            // Merge oltp config with existing config
            Object.keys(oltpConfig).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(existingConfig, key)) {
                    // Only update if the field exists in the original config
                    // Update even if the new value is empty/null (user might want to clear it)
                    filteredNewConfig[key] = oltpConfig[key];
                }
            });

            // Merge filtered new config with existing config (only update existing fields, preserve all others)
            let mergedConfig = {
                ...existingConfig,
                ...filteredNewConfig
            };

            // Check if tenant_id or extract fields exist in the original config
            let edgeExtractConfigAvailable = false;
            if (extractConfigYaml) {
                if (existingConfig.tenant_id !== undefined || existingConfig.extract !== undefined) {
                    edgeExtractConfigAvailable = true;

                    // Delete tenant_id and extract from config if extract config is provided and they exist in original config
                    delete mergedConfig.tenant_id;
                    delete mergedConfig.extract;
                }
            }

            // Convert merged config back to YAML string with anchor format
            // Uses stored anchor names if available, otherwise falls back to field name
            const formatYamlWithAnchor = (key, value) => {
                // Get anchor name for this field, fallback to key if not found
                const anchorName = jobAnchors[key] || key;

                // Handle simple values (string, number, boolean, null)
                if (value === null || value === undefined) {
                    return `${key}: &${anchorName} `;
                } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                    return `${key}: &${anchorName} ${value}`;
                } else if (Array.isArray(value)) {
                    // For arrays, format as JSON string inline
                    const jsonStr = JSON.stringify(value);
                    return `${key}: &${anchorName} ${jsonStr}`;
                } else if (typeof value === 'object') {
                    // For objects, format as JSON string inline
                    const jsonStr = JSON.stringify(value);
                    return `${key}: &${anchorName} ${jsonStr}`;
                }
                return `${key}: &${anchorName} ${String(value)}`;
            };

            let updatedSection = Object.keys(mergedConfig)
                .map(key => formatYamlWithAnchor(key, mergedConfig[key]))
                .join('\n');

            // append the easy mode edge extract yaml with anchors
            if (extractConfigYaml && edgeExtractConfigAvailable) {
                try {
                    const lines = extractConfigYaml.split('\n');
                    const processedLines = [];

                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i];
                        const trimmedLine = line.trim();

                        // Check if this line is a field we need to add anchor to
                        if (trimmedLine.startsWith('tenant_id:') || trimmedLine.startsWith('extract:')) {
                            const fieldName = trimmedLine.split(':')[0].trim();
                            const anchorName = jobAnchors[fieldName] || fieldName;

                            // Check if anchor is already inline
                            if (trimmedLine.includes('&')) {
                                processedLines.push(line);
                            } else {
                                // Check if next line has an anchor
                                if (i + 1 < lines.length && lines[i + 1].trim().startsWith('&')) {
                                    // Combine current line with anchor from next line
                                    const indent = line.match(/^(\s*)/)[1];
                                    processedLines.push(`${indent}${fieldName}: &${anchorName}`);
                                    i++; // Skip the next line since we've merged it
                                } else {
                                    // Add anchor inline to current line
                                    processedLines.push(line.replace(new RegExp(`^\\s*${fieldName}:`), `${line.match(/^(\s*)/)[1]}${fieldName}: &${anchorName}`));
                                }
                            }
                        } else {
                            processedLines.push(line);
                        }
                    }

                    const extractYamlWithAnchors = processedLines.join('\n');
                    updatedSection = updatedSection + '\n' + extractYamlWithAnchors;
                } catch (e) {
                    console.warn('Failed to add anchors to extract config YAML:', e);
                    // Fallback: append as-is if parsing fails
                    updatedSection = updatedSection + '\n' + extractConfigYaml;
                }
            }

            // Reconstruct the full vars.yaml content
            const updatedContent = beforeSection +
                startMarker + '\n' +
                '# Managed by UI - manual edits may be overwritten\n' +
                updatedSection + '\n' +
                endMarker +
                afterSection;
            return updatedContent;
        },
        async updateJobsConfig(newConfig, oltpConfig, extractConfigYaml = null) {
            if (!this.jobs || this.jobs.length === 0) {
                return {
                    total: 0,
                    successful: 0
                };
            }

            // Filter jobs that have vars.yaml with config markers
            const jobsToUpdate = this.jobs.filter(job => {
                if (!job?.jobDetails?.system_files) {
                    return false;
                }
                const varsYamlFile = job.jobDetails.system_files.find(
                    file => file.relative_path === 'vars.yaml'
                );
                if (!varsYamlFile?.content) {
                    return false;
                }
                // Check if markers exist
                const hasMarkers = varsYamlFile.content.includes(this.uiMarkerStart) &&
                    varsYamlFile.content.includes(this.uiMarkerEnd);
                return hasMarkers;
            });

            if (jobsToUpdate.length === 0) {
                return {
                    total: 0,
                    successful: 0
                };
            }

            const cid = this.$store.state.currentClient?.cid;
            const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
            const lakeviewUrl = this.$store.state.lakeviewUrl;
            const accessToken = this.$store.state.accessToken;

            // Reset config diff and conflict details
            this.configDiff = [];
            this.conflictDetails = null;

            // Construct payload and update the current jobs info
            const currentJobsMap = {};
            jobsToUpdate.forEach((job) => {
                const varsYamlFile = job.jobDetails.system_files.find(
                    file => file.relative_path === 'vars.yaml'
                );

                const jobName = job.jobDetails?.name || job.jobId;

                // Update the vars.yaml content
                const updatedContent = this.updateConfigSection(
                    jobName,
                    varsYamlFile.content,
                    newConfig,
                    oltpConfig,
                    extractConfigYaml
                );

                // Update the job object
                const updatedJob = {
                    ...job.jobDetails,
                    system_files: job.jobDetails.system_files.map(file => {
                        if (file.relative_path === 'vars.yaml') {
                            return {
                                ...file,
                                content: updatedContent
                            };
                        }
                        return file;
                    })
                };
                currentJobsMap[updatedJob.name] = updatedJob;
            });
            this.currentJobsInfo = currentJobsMap;

            // Get all jobs from remote to check for conflicts
            const getJobsPromises = jobsToUpdate.map((job) => {
                const jobName = job.jobDetails?.name || job.jobId;
                return axios.get(
                    `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${jobName}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                ).then(response => {
                    return {
                        jobName: jobName,
                        data: response.data
                    };
                }).catch(error => {
                    return {
                        jobName: jobName,
                        error: error
                    };
                });
            });

            // Wait for all get jobs to complete using Promise.allSettled
            const results = await Promise.allSettled(getJobsPromises);
            const remoteJobsMap = {};
            let hasConflicts = false;
            const diff = [];

            results.forEach((result, index) => {
                const job = jobsToUpdate[index];
                const jobName = job.jobDetails?.name || job.jobId;

                if (result.status === 'fulfilled' && result.value.data) {
                    const remoteJob = result.value.data;
                    remoteJobsMap[jobName] = remoteJob;

                    const currentJob = currentJobsMap[jobName];

                    // Check if job was updated since we last fetched it
                    if (remoteJob.updated_at !== job.jobDetails.updated_at) {
                        hasConflicts = true;

                        // Compare all files (system_files and user_files) between current and remote
                        ["system_files", "user_files"].forEach((fileType) => {
                            const currentFiles = currentJob[fileType] || [];
                            const remoteFiles = remoteJob[fileType] || [];

                            // Check files that exist in remote (may have been updated or are new)
                            remoteFiles.forEach((remoteFile) => {
                                const currentFile = currentFiles.find(
                                    f => f.relative_path === remoteFile.relative_path
                                );

                                // If file doesn't exist in current, it's a new file in remote
                                if (!currentFile) {
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: remoteFile.relative_path,
                                            content: ''
                                        },
                                        current: {
                                            relative_path: remoteFile.relative_path,
                                            content: remoteFile.content || ''
                                        }
                                    });
                                } else if (currentFile.content !== remoteFile.content) {
                                    // File exists in both but content differs
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: currentFile.relative_path,
                                            content: currentFile.content || ''
                                        },
                                        current: {
                                            relative_path: remoteFile.relative_path,
                                            content: remoteFile.content || ''
                                        }
                                    });
                                }
                            });

                            // Check files that exist in current but not in remote (may have been deleted)
                            currentFiles.forEach((currentFile) => {
                                const remoteFile = remoteFiles.find(
                                    f => f.relative_path === currentFile.relative_path
                                );

                                if (!remoteFile) {
                                    // File exists in current but not in remote (deleted in remote)
                                    diff.push({
                                        jobName: jobName,
                                        prev: {
                                            relative_path: currentFile.relative_path,
                                            content: currentFile.content || ''
                                        },
                                        current: {
                                            relative_path: currentFile.relative_path,
                                            content: ''
                                        }
                                    });
                                }
                            });
                        });
                    }
                }
            });

            this.remoteJobsInfo = remoteJobsMap;
            this.configDiff = diff;

            // If there are conflicts, show the config changes tab
            if (hasConflicts && diff.length > 0) {
                this.tabItems[1].visible = true;
                // Get the most recent update details
                const latestRemoteJob = Object.values(remoteJobsMap)
                    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
                if (latestRemoteJob) {
                    this.conflictDetails = {
                        updated_by: latestRemoteJob.updated_by || 'Unknown',
                        updated_at: latestRemoteJob.updated_at
                    };
                }
                // Switch to config changes tab
                this.setActiveTab('config-changes');
                return {
                    total: jobsToUpdate.length,
                    successful: 0,
                    hasConflicts: true
                };
            }

            // No conflicts or force update - proceed with actual updates
            const updatePromises = Object.values(currentJobsMap).map((updatedJob) => {
                return axios.put(
                    `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${updatedJob.name}`,
                    updatedJob,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
            });

            // Wait for all updates to complete using Promise.allSettled
            const updateResults = await Promise.allSettled(updatePromises);
            let successfulUpdates = 0;

            updateResults.forEach((result) => {
                if (result.status === 'fulfilled') {
                    successfulUpdates++;
                } else {
                    console.error('Failed to update job: ', result.reason.response?.data);
                }
            });

            return {
                total: jobsToUpdate.length,
                successful: successfulUpdates,
                hasConflicts: false
            };
        },
        async executePipelineJob() {
            if (!this.pipelineJob || !this.pipelineJob.jobDetails) {
                console.log('No pipeline job found..');
                return;
            }
            try {
                const cid = this.$store.state.currentClient?.cid;
                const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
                const response = await axios.post(
                    `${this.$store.state.lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${this.pipelineJob.jobDetails.name}/executions`, {},
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );
                this.$router.push({
                    name: "Sandbox Job Execution Details",
                    params: {
                        cid: cid,
                        sandboxId: sandboxName,
                        jobId: this.pipelineJob.jobDetails.name,
                        executionId: response.data.execution_id
                    }
                });
            } catch (error) {
                this.editError = error.response?.data?.error || error.message;
                this.$store.dispatch("addToast", {
                    message: error.response?.data?.error || error.message || "Failed to execute pipeline job. Please contact support.",
                    type: "error",
                });
            }
        },
        async onReloadConfig() {
            this.closeConfigChangesTab();

            // reload jobs in detail view
            if (this.modalData?.onRefreshJobs && typeof this.modalData.onRefreshJobs === 'function') {
                this.modalData.onRefreshJobs();
            }

            // reload jobs in modal
            await this.refreshJobsData();
            this.loadConfig();
            this.initializePromoteTab(); // to update vars.yaml content in promote tab  
        },
        async onOverwriteConfig() {
            this.editError = "";
            this.saveInProgress = true;
            this.saveAndRunInProgress = false;
            this.overWriteInProgress = true;

            const cid = this.$store.state.currentClient?.cid;
            const sandboxName = this.deployment?.sandbox || this.modalData?.sandboxName;
            const lakeviewUrl = this.$store.state.lakeviewUrl;
            const accessToken = this.$store.state.accessToken;

            try {
                // Create update promises for all jobs in currentJobsInfo
                const updatePromises = Object.values(this.currentJobsInfo).map((updatedJob) => {
                    return axios.put(
                        `${lakeviewUrl}/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${updatedJob.name}`,
                        updatedJob,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        }
                    );
                });

                // Wait for all updates to complete using Promise.allSettled
                const updateResults = await Promise.allSettled(updatePromises);
                let successfulUpdates = 0;
                const total = Object.keys(this.currentJobsInfo).length;

                updateResults.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        successfulUpdates++;
                    } else {
                        console.error('Failed to update job: ', result.reason.response?.data);
                    }
                });

                // Show toasts based on results
                if (successfulUpdates === total) {
                    this.$store.dispatch("addToast", {
                        message: `Configuration overwritten successfully for all ${total} job(s).`,
                        type: "success",
                    });

                    // update jobs in detail view
                    if (this.modalData?.onRefreshJobs && typeof this.modalData.onRefreshJobs === 'function') {
                        this.modalData.onRefreshJobs();
                    }

                    // update jobs in modal
                    await this.refreshJobsData();
                    this.loadConfig();
                    this.initializePromoteTab(); // to update vars.yaml content in promote tab

                    // Hide config changes tab after successful overwrite
                    this.tabItems[1].visible = false;
                    this.setActiveTab('edit');
                    // Clear the diff data
                    this.configDiff = [];
                    this.conflictDetails = null;
                } else if (successfulUpdates > 0) {
                    this.editError = `Failed to overwrite ${total - successfulUpdates} out of ${total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: `Configuration overwritten for ${successfulUpdates} job(s), but ${total - successfulUpdates} job(s) failed.`,
                        type: "warning",
                    });
                } else {
                    this.editError = `Failed to overwrite all ${total} job(s)`;
                    this.$store.dispatch("addToast", {
                        message: this.editError,
                        type: "error",
                    });
                }
            } catch (error) {
                this.editError = error.response?.data?.error || error.message || "Failed to overwrite configuration";
                this.$store.dispatch("addToast", {
                    message: this.editError || "Something went wrong when overwriting configuration. Please contact support.",
                    type: "error",
                });
            } finally {
                this.saveInProgress = false;
                this.saveAndRunInProgress = false;
                this.overWriteInProgress = false;
            }
        },
        closeConfigChangesTab() {
            // Hide the config changes tab and switch back to edit tab
            this.tabItems[1].visible = false;
            this.setActiveTab('edit');
            // Clear the diff data
            this.configDiff = [];
            this.conflictDetails = null;
        },
        async readSandboxJobDetails(sandboxName, jobName) {
            const cid = this.$store.state.currentClient?.cid;
            return axios.get(this.$store.state.lakeviewUrl + `/v1/cids/${cid}/sandboxes/${sandboxName}/jobs/${jobName}`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            })
        },
        async refreshJobsData() {
            if (!this.jobNames || this.jobNames.length === 0) {
                console.log('No job names to refresh jobs data...');
                this.jobs = [];
                return;
            }

            this.jobReloadInProgress = true;
            try {
                const details = await Promise.all(
                    this.jobNames.map(async (jobId) => {
                        try {
                            const response = await this.readSandboxJobDetails(this.deployment.sandbox, jobId);
                            return {
                                jobId: jobId,
                                jobDetails: response?.data || null,
                                error: null
                            };
                        } catch (error) {
                            return {
                                jobId: jobId,
                                jobDetails: null,
                                error: error.response?.data?.error || error.message
                            };
                        }
                    })
                );
                // Replace the entire array to ensure Vue detects the change
                this.jobs = details;
            } catch (error) {
                this.jobs = [];
                console.error('Error refreshing jobs data:', error);
            } finally {
                this.jobReloadInProgress = false;
            }
        },

        // promotion functions
        async initializePromoteTab() {
            if (!this.jobs || this.jobs.length === 0) {
                console.log('No jobs to initialize promote tab...');
                return;
            }

            // Process all jobs in parallel using Promise.all
            await Promise.all(
                this.jobs.map(async (job) => {
                    const jobDetails = job.jobDetails;
                    const promoteObject = {
                        dst_sandbox_name: this.destinationSandbox || this.$store.state.sandbox.prodV2Sandboxes.length ? this.$store.state.sandbox.prodV2Sandboxes[0].name : "",
                        dst_job_name: jobDetails.name,
                        description: `Promote - '${jobDetails.name}'`,
                        src_job_run_id: "",
                        vars: jobDetails?.system_files?.find(file => file.relative_path === 'vars.yaml')?.content || "",
                        meters: [],
                    }

                    let executions = await this.getLatestSuccessfulExecution(jobDetails.name);
                    if (Array.isArray(executions) && executions.length > 0) {
                        promoteObject.src_job_run_id = executions[0].id;
                    }

                    this.promotions[jobDetails.name] = JSON.parse(JSON.stringify(promoteObject));
                })
            );
        },
        async getLatestSuccessfulExecution(jobId) {
            let sandbox = this.deployment.sandbox
            let response = await axios.get(`${this.$store.state.lakeviewUrl}/v1/cids/${this.$store.state.currentClient.cid}/sandboxes/${sandbox}/jobs/${jobId}/executions?status=SUCCEEDED&limit=1`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            })
            return response.data?.length > 0 ? response.data : [];
        },
        preparePromotionPayload(jobId) {
            if (!this.promotions[jobId]) {
                return null;
            }
            const promotion = JSON.parse(JSON.stringify(this.promotions[jobId]));

            // Remove empty vars (Codemirror adds empty string on initial editor load)
            if (promotion.vars === '') {
                delete promotion.vars;
            }

            // Handle meters - add meters if not detach_meters and meters exist
            const meters = promotion.meters || [];
            if (!promotion.detach_meters && meters.length > 0) {
                promotion.meters = meters;
            } else {
                delete promotion.meters;
            }

            return promotion;
        },
        async getJobExecutionConfig(sandbox, job, jobRunId) {
            let responseObj = {
                jobId: job,
                config: null,
                error: null,
            }
            await axios.get(`${this.$store.state.lakeviewUrl}/v1/cids/${this.$store.state.currentClient.cid}/sandboxes/${sandbox}/jobs/${job}/executions/${jobRunId}/configs`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            })
                .then((response) => {
                    responseObj.config = response.data || null;
                })
                .catch((error) => {
                    console.error(error);
                    responseObj.error = error;
                });
            return responseObj;
        },
        async promoteRequiredJobs() {
            try {
                this.promotionInProgress = true;

                // for all jobs, promote only if there is a successful execution and if it is not updated in prod sandbox job or prod sandbox job not found
                let jobsWithoutExecution = []; // jobs without successful execution
                let jobsWithoutExecutionConfig = []; // jobs with successful execution but unable to get execution config
                let currentJobConfigs = {}; // current job configs (key is jobId, value is config). Need this to compare with prod sandbox job config.
                let prodJobConfigs = {}; // production sandbox job configs (key is jobId, value is config). Need this to compare with current sandbox job config.
                let totalPromotions = 0;
                const checkCurrentJobConfigPromises = Object.entries(this.promotions).map(async ([jobId, promotion]) => {
                    let currentSandboxId = this.deployment.sandbox;
                    let latestRunId = promotion.src_job_run_id;
                    if (!latestRunId) {
                        throw new Error(`No successful execution found for job - ${jobId}`);
                    }
                    let response = await this.getJobExecutionConfig(currentSandboxId, jobId, latestRunId);
                    if (response.error || !response.config) {
                        throw new Error(`No execution config found for job - ${jobId}`);
                    }
                    return response;
                })
                const results1 = await Promise.allSettled(checkCurrentJobConfigPromises);

                for (const result of results1) {
                    if (result.status === 'fulfilled') {
                        currentJobConfigs[result.value.jobId] = result.value.config;
                    } else {
                        let err = result.reason.message;
                        if (err.includes("No successful execution found for job")) {
                            let job = err.split("-")[1].trim();
                            jobsWithoutExecution.push(job);
                        } else if (err.includes("No execution config found for job")) {
                            let job = err.split("-")[1].trim();
                            jobsWithoutExecutionConfig.push(job);
                        }
                    }
                }

                if (jobsWithoutExecution.length > 0 || jobsWithoutExecutionConfig.length > 0) {
                    if (jobsWithoutExecution.length > 0) {
                        this.$store.dispatch("addToast", {
                            message: `Following jobs do not have successful executions - ${jobsWithoutExecution.join(', ')}.\nClick 'Run' and try promoting again.`,
                            type: "error",
                        });
                    }
                    if (jobsWithoutExecutionConfig.length > 0) {
                        this.$store.dispatch("addToast", {
                            message: `Execution config not found for the following jobs - ${jobsWithoutExecutionConfig.join(', ')}.\nClick 'Run' and try promoting again.`,
                            type: "error",
                        });
                    }
                    this.promotionInProgress = false;
                    return;
                }

                // get all the destination sandbox job configs
                const checkProdJobConfigPromises = Object.entries(this.promotions).map(async ([jobId, promotion]) => {
                    let sandbox = promotion.dst_sandbox_name;
                    let job = promotion.dst_job_name;

                    await this.readJob({ sandboxName: sandbox, jobName: job })
                        .then((response) => {
                            prodJobConfigs[jobId] = response.data || null;
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                });
                await Promise.allSettled(checkProdJobConfigPromises);

                // check if any job config is different from prod sandbox job config
                let successfulPromotions = 0;
                const compareAndPromotePromises = Object.entries(this.promotions).map(async ([jobId, promotion]) => {
                    let currentConfig = currentJobConfigs[jobId];
                    let prodConfig = prodJobConfigs[jobId];
                    let isConfigDifferent = this.compareConfigs(currentConfig, prodConfig);

                    if (isConfigDifferent) {
                        // promote the job
                        console.log("Promoting job - ", jobId);
                        totalPromotions++;
                        const promotionPayload = this.preparePromotionPayload(jobId);
                        if (!promotionPayload) {
                            throw new Error(`Failed to prepare promotion payload for job - ${jobId}`);
                        }
                        await axios.post(`${this.$store.state.lakeviewUrl}/v1/cids/${this.$store.state.currentClient.cid}/promotions`, promotionPayload, {
                            headers: {
                                Authorization: `Bearer ${this.$store.state.accessToken}`,
                            },
                        }).then(() => {
                            successfulPromotions++;
                        }).catch((error) => {
                            console.error(error);
                            throw new Error(`Failed to promote job - ${jobId}`);
                        });
                    }
                });

                await Promise.allSettled(compareAndPromotePromises);
                if (totalPromotions === 0) {
                    this.$store.dispatch("addToast", {
                        message: `No changes detected.`,
                        type: "info",
                    });
                } else if (successfulPromotions === totalPromotions) {
                    this.$store.dispatch("addToast", {
                        message: `Promotion requests successfully raised for ${successfulPromotions} job(s).`,
                        type: "success",
                    });
                } else if (successfulPromotions > 0) {
                    this.$store.dispatch("addToast", {
                        message: `Promotion requests successfully raised for ${successfulPromotions} job(s), but ${totalPromotions - successfulPromotions} job(s) failed.`,
                        type: "warning",
                    });
                } else {
                    this.$store.dispatch("addToast", {
                        message: `Promotion requests failed for all job(s).`,
                        type: "error",
                    });
                }
            } catch (error) {
                console.error('Error promoting required jobs:', error);
                this.$store.dispatch("addToast", {
                    message: "Failed to promote required jobs. Please contact support.",
                    type: "error",
                });
            } finally {
                this.promotionInProgress = false;
            }
        },
        compareConfigs(currentConfig, prodConfig) {
            if (!currentConfig || !prodConfig) {
                return true;
            }
            if ((currentConfig.system_files?.length !== prodConfig.system_files?.length) || (currentConfig.user_files?.length !== prodConfig.user_files?.length)) {
                return true;
            }
            for (const systemFile of currentConfig.system_files) {
                const prodSystemFile = prodConfig.system_files.find(file => file.relative_path === systemFile.relative_path);
                // deep compare the contents of the two files
                if (!prodSystemFile || YAML.stringify(YAML.parse(prodSystemFile.content)) !== YAML.stringify(YAML.parse(systemFile.content))) {
                    return true;
                }
            }
            for (const userFile of currentConfig.user_files) {
                const prodUserFile = prodConfig.user_files.find(file => file.relative_path === userFile.relative_path);
                // deep compare the contents of the two files
                if (!prodUserFile || YAML.stringify(YAML.parse(prodUserFile.content)) !== YAML.stringify(YAML.parse(userFile.content))) {
                    return true;
                }
            }
            return false;
        }
    },
    watch: {
        destinationSandbox(newVal) {
            // for all promotion onjects
            Object.values(this.promotions).forEach(promotion => {
                promotion.dst_sandbox_name = newVal;
            });
        }
    }
}
</script>

<style scoped>
.custom-modal-tabs {
    border-bottom: 2px solid #e9ecef;
    margin-bottom: 1rem;
    gap: 0;
}

.custom-modal-tabs .nav-item {
    margin: 0;
    margin-right: 0;
    padding: 0;
}

.custom-modal-tabs .nav-link {
    color: #6c757d;
    background-color: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.6rem 0.75rem;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    display: flex;
    align-items: center;
}

.custom-modal-tabs .nav-link:hover {
    color: #495057;
    background-color: #f8f9fa;
    border-bottom-color: #dee2e6;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
}

.custom-modal-tabs .nav-link.active {
    color: #0d6efd;
    background-color: #e2edff;
    border-bottom-color: #0d6efd;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    font-weight: 600;
}

.custom-modal-tabs .nav-link i {
    font-size: 0.9rem;
}


.tab-content {
    min-height: 200px;
}

.tab-pane {
    display: none;
}

.tab-pane.show.active {
    display: block;
    animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(5px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.edit-tab-content {
    padding-top: 0.5rem;
}

.config-changes-tab-content {
    padding-top: 0.5rem;
}

.config-header {
    color: #2C7BE5;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
}
</style>